import { StyleSheet, Font } from "@react-pdf/renderer";

// Register Inter font for the PDF (supports Czech/Slovak diacritics: ščřžýáíéěůú)
Font.register({
  family: "Inter",
  fonts: [
    {
      src: "/fonts/Inter-VariableFont_opsz,wght.ttf",
      fontWeight: "normal",
    },
    {
      src: "/fonts/Inter-VariableFont_opsz,wght.ttf",
      fontWeight: "bold",
    },
  ],
});

// Disable default hyphenation which uses English rules and can break Czech/Slovak words
Font.registerHyphenationCallback((word) => [word]);

// Color palette
const colors = {
  primary: "#1976d2",
  secondary: "#9c27b0",
  success: "#4caf50",
  error: "#f44336",
  warning: "#ff9800",
  text: {
    primary: "#212121",
    secondary: "#757575",
    disabled: "#9e9e9e",
  },
  background: {
    default: "#ffffff",
    paper: "#f5f5f5",
  },
  border: "#e0e0e0",
  men: "#2196f3",
  women: "#9c27b0",
};

export const styles = StyleSheet.create({
  // Page styles
  page: {
    padding: 40,
    fontFamily: "Inter",
    fontSize: 10,
    color: colors.text.primary,
    backgroundColor: colors.background.default,
  },

  coverPage: {
    padding: 40,
    fontFamily: "Inter",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },

  // Cover page elements
  coverLogo: {
    width: 150,
    height: 150,
    marginBottom: 30,
    objectFit: "contain",
  },

  coverCompanyName: {
    fontSize: 28,
    fontWeight: "bold",
    color: colors.text.primary,
    marginBottom: 20,
    textAlign: "center",
  },

  coverTitle: {
    fontSize: 22,
    color: colors.primary,
    marginBottom: 10,
    textAlign: "center",
  },

  coverSubtitle: {
    fontSize: 14,
    color: colors.text.secondary,
    marginBottom: 40,
    textAlign: "center",
  },

  coverDate: {
    fontSize: 12,
    color: colors.text.secondary,
    textAlign: "center",
  },

  // Header and footer
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },

  headerLogo: {
    width: 60,
    height: 60,
    objectFit: "contain",
  },

  headerTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: colors.text.primary,
  },

  headerDate: {
    fontSize: 9,
    color: colors.text.secondary,
  },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },

  footerText: {
    fontSize: 8,
    color: colors.text.secondary,
  },

  pageNumber: {
    fontSize: 8,
    color: colors.text.secondary,
  },

  // Section styles
  section: {
    marginBottom: 20,
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: colors.primary,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },

  sectionSubtitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: colors.text.primary,
    marginBottom: 8,
    marginTop: 12,
  },

  // Text styles
  text: {
    fontSize: 10,
    lineHeight: 1.5,
    color: colors.text.primary,
    marginBottom: 6,
  },

  textSmall: {
    fontSize: 9,
    lineHeight: 1.4,
    color: colors.text.secondary,
  },

  textBold: {
    fontSize: 10,
    fontWeight: "bold",
    color: colors.text.primary,
  },

  paragraph: {
    fontSize: 10,
    lineHeight: 1.6,
    color: colors.text.primary,
    marginBottom: 10,
    textAlign: "justify",
  },

  // Table styles
  table: {
    width: "100%",
    marginBottom: 15,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: colors.primary,
    paddingVertical: 8,
    paddingHorizontal: 6,
  },

  tableHeaderCell: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#ffffff",
    flex: 1,
    textAlign: "center",
  },

  tableHeaderCellLeft: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#ffffff",
    flex: 1,
    textAlign: "left",
  },

  tableRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },

  tableRowAlt: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.paper,
  },

  tableCell: {
    fontSize: 9,
    color: colors.text.primary,
    flex: 1,
    textAlign: "center",
  },

  tableCellLeft: {
    fontSize: 9,
    color: colors.text.primary,
    flex: 1,
    textAlign: "left",
  },

  tableCellBold: {
    fontSize: 9,
    fontWeight: "bold",
    color: colors.text.primary,
    flex: 1,
    textAlign: "center",
  },

  // Metric cards
  metricsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
    gap: 10,
  },

  metricCard: {
    flex: 1,
    padding: 12,
    backgroundColor: colors.background.paper,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.border,
  },

  metricLabel: {
    fontSize: 9,
    color: colors.text.secondary,
    marginBottom: 4,
  },

  metricValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: colors.primary,
  },

  metricValuePositive: {
    fontSize: 18,
    fontWeight: "bold",
    color: colors.success,
  },

  metricValueNegative: {
    fontSize: 18,
    fontWeight: "bold",
    color: colors.error,
  },

  // Chart image container
  chartContainer: {
    marginVertical: 12,
    alignItems: "center",
  },

  chartContainerLarge: {
    marginVertical: 16,
    alignItems: "center",
    width: "100%",
  },

  chartImage: {
    maxWidth: "100%",
    height: "auto",
    objectFit: "contain",
  },

  chartImageLarge: {
    width: 500,
    height: 380,
    objectFit: "contain",
  },

  chartImageMedium: {
    width: 440,
    height: 320,
    objectFit: "contain",
  },

  chartImageSmall: {
    width: 360,
    height: 260,
    objectFit: "contain",
  },

  // Pie chart specific - standardized size
  pieChartContainer: {
    marginVertical: 16,
    alignItems: "center",
    justifyContent: "center",
  },

  pieChartImage: {
    width: 380,
    height: 340,
    objectFit: "contain",
  },

  chartTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: colors.text.primary,
    marginBottom: 10,
    textAlign: "center",
  },

  chartSubtitle: {
    fontSize: 10,
    color: colors.text.secondary,
    marginBottom: 8,
    textAlign: "center",
  },

  // Legend styles
  legend: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 20,
    marginTop: 10,
  },

  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },

  legendColorMen: {
    width: 12,
    height: 12,
    backgroundColor: colors.men,
    borderRadius: 2,
  },

  legendColorWomen: {
    width: 12,
    height: 12,
    backgroundColor: colors.women,
    borderRadius: 2,
  },

  legendText: {
    fontSize: 8,
    color: colors.text.secondary,
  },

  // Status badges
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    alignSelf: "flex-start",
  },

  badgeSuccess: {
    backgroundColor: "#e8f5e9",
  },

  badgeWarning: {
    backgroundColor: "#fff3e0",
  },

  badgeError: {
    backgroundColor: "#ffebee",
  },

  badgeText: {
    fontSize: 8,
    fontWeight: "bold",
  },

  badgeTextSuccess: {
    color: colors.success,
  },

  badgeTextWarning: {
    color: colors.warning,
  },

  badgeTextError: {
    color: colors.error,
  },

  // Page break utilities
  pageBreakBefore: {
    break: "before" as const,
  },

  pageBreakAfter: {
    break: "after" as const,
  },

  avoidBreak: {
    break: "avoid" as const,
  },

  // Flexbox utilities
  row: {
    flexDirection: "row",
  },

  column: {
    flexDirection: "column",
  },

  flexGrow: {
    flexGrow: 1,
  },

  alignCenter: {
    alignItems: "center",
  },

  justifyCenter: {
    justifyContent: "center",
  },

  justifyBetween: {
    justifyContent: "space-between",
  },

  // Spacing utilities
  mt4: { marginTop: 4 },
  mt8: { marginTop: 8 },
  mt12: { marginTop: 12 },
  mt16: { marginTop: 16 },
  mt20: { marginTop: 20 },
  mb4: { marginBottom: 4 },
  mb8: { marginBottom: 8 },
  mb12: { marginBottom: 12 },
  mb16: { marginBottom: 16 },
  mb20: { marginBottom: 20 },
});

export { colors };
